import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { useTranslate } from '@tolgee/react';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect } from 'react';
import { useState } from 'react';

export interface Step1Data {
  gender: number;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  firstname: string;
  lastname: string;
  birthday: string;
}

interface Step1BasicInfoProps {
  data: Step1Data;
  onChange: (data: Partial<Step1Data>) => void;
  onValidChange: (isValid: boolean) => void;
}

export function Step1BasicInfo({ data, onChange, onValidChange }: Step1BasicInfoProps) {
  const { t } = useTranslate();
  const [showPassword, setShowPassword] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onValidChange is stable from parent
  useEffect(() => {
    const isValid =
      data.email.length > 0 &&
      data.email.includes('@') &&
      data.username.length >= 3 &&
      /^[a-zA-Z0-9_]+$/.test(data.username) &&
      data.password.length >= 8 &&
      data.password === data.confirmPassword &&
      data.firstname.length > 0 &&
      data.lastname.length > 0 &&
      data.birthday.length > 0;

    onValidChange(isValid);
  }, [
    data.email,
    data.username,
    data.password,
    data.confirmPassword,
    data.firstname,
    data.lastname,
    data.birthday,
  ]);

  const handleChange = (field: keyof Step1Data, value: string | number) => {
    onChange({ [field]: value });
  };

  // Calculate max date (18 years ago)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="space-y-4">
      {/* Gender Selection */}
      <div className="space-y-3">
        <Label className="text-base font-medium">{t('signup.fields.gender')}</Label>
        <RadioGroup
          value={String(data.gender)}
          onValueChange={(value) => handleChange('gender', Number.parseInt(value))}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="1" id="male" />
            <Label htmlFor="male" className="cursor-pointer">
              {t('signup.genders.male')}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="2" id="female" />
            <Label htmlFor="female" className="cursor-pointer">
              {t('signup.genders.female')}
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email">{t('signup.fields.email')} *</Label>
        <Input
          id="email"
          type="email"
          placeholder="nom@exemple.com"
          value={data.email}
          onChange={(e) => handleChange('email', e.target.value)}
          autoComplete="email"
          required
        />
      </div>

      {/* Username */}
      <div className="space-y-2">
        <Label htmlFor="username">{t('signup.fields.username')} *</Label>
        <Input
          id="username"
          type="text"
          placeholder={t('signup.placeholders.username')}
          value={data.username}
          onChange={(e) =>
            handleChange('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))
          }
          pattern="^[a-zA-Z0-9_]+$"
          required
        />
        <p className="text-xs text-muted-foreground">{t('signup.hints.username')}</p>
      </div>

      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="password">{t('signup.fields.password')} *</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder={t('signup.placeholders.password')}
            value={data.password}
            onChange={(e) => handleChange('password', e.target.value)}
            autoComplete="new-password"
            required
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t('signup.hints.password')}</p>
      </div>

      {/* Confirm Password */}
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">{t('signup.fields.confirmPassword')} *</Label>
        <Input
          id="confirmPassword"
          type={showPassword ? 'text' : 'password'}
          placeholder={t('signup.placeholders.confirmPassword')}
          value={data.confirmPassword}
          onChange={(e) => handleChange('confirmPassword', e.target.value)}
          autoComplete="new-password"
          required
        />
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstname">{t('signup.fields.firstname')} *</Label>
          <Input
            id="firstname"
            type="text"
            placeholder={t('signup.placeholders.firstname')}
            value={data.firstname}
            onChange={(e) => handleChange('firstname', e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastname">{t('signup.fields.lastname')} *</Label>
          <Input
            id="lastname"
            type="text"
            placeholder={t('signup.placeholders.lastname')}
            value={data.lastname}
            onChange={(e) => handleChange('lastname', e.target.value)}
            required
          />
        </div>
      </div>

      {/* Birthday */}
      <div className="space-y-2">
        <Label htmlFor="birthday">{t('signup.fields.birthday')} *</Label>
        <Input
          id="birthday"
          type="date"
          value={data.birthday}
          max={maxDateStr}
          onChange={(e) => handleChange('birthday', e.target.value)}
          required
        />
      </div>

      <p className="text-xs text-muted-foreground pt-2">* {t('signup.requiredFields')}</p>
    </div>
  );
}
